import { useState } from "react";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[\d\s\-\+\(\)]{6,20}$/;

function getFieldError(key, value, allErrors = {}) {
    const v = (value || "").trim();
    switch (key) {
        case "name":
            if (!v) return "請輸入姓名";
            if (v.length < 2) return "姓名至少需要 2 個字";
            return "";
        case "email":
            if (!v) return "請輸入 Email";
            if (!emailRegex.test(v)) return "請輸入有效的 Email 格式";
            return "";
        case "phone":
            if (v && !phoneRegex.test(v)) return "請輸入有效的電話號碼";
            return "";
        default:
            if (!v) return "此欄位為必填";
            return "";
    }
}

export default function RegisterForm({ onDone, t }) {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [formTouched, setFormTouched] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [merchant, setMerchant] = useState({ name: "", branch: "", region: "", address: "" });
    const [merchantTouched, setMerchantTouched] = useState({});
    const [merchantErrors, setMerchantErrors] = useState({});
    const [password, setPassword] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [pwTouched, setPwTouched] = useState(false);
    const [confirmTouched, setConfirmTouched] = useState(false);
    const [globalError, setGlobalError] = useState("");
    const [done, setDone] = useState(false);

    const steps = ["基本資料", "商戶資料", "設定密碼"];
    const inputStyle = {
        background: t.inputBg, border: `1px solid ${t.border}`, color: t.text,
        borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none",
        width: "100%", boxSizing: "border-box"
    };

    const pwStrength = password.length >= 10 && /[A-Z]/.test(password) && /[0-9]/.test(password) ? 4
        : password.length >= 8 && /[0-9]/.test(password) ? 3
            : password.length >= 6 ? 2
                : password.length > 0 ? 1 : 0;
    const pwColors = ["", t.danger, "#f59e0b", "#3b82f6", t.success];
    const pwLabels = ["", "太短", "普通", "中等", "強"];

    const inputBorder = (hasError) => ({
        ...inputStyle,
        border: hasError ? `1px solid ${t.danger}` : `1px solid ${t.border}`,
    });

    const FieldError = ({ msg }) => msg
        ? <p style={{ color: t.danger, fontSize: 11, margin: "3px 0 0" }}>⚠️ {msg}</p>
        : null;

    // ── Step 1 ──────────────────────────────────────────────
    const handleFormChange = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        const err = getFieldError(key, val);
        setFormErrors((prev) => ({ ...prev, [key]: err }));
    };

    const handleFormBlur = (key) => {
        setFormTouched((prev) => ({ ...prev, [key]: true }));
        setFormErrors((prev) => ({ ...prev, [key]: getFieldError(key, form[key]) }));
    };

    const step1Valid = () => {
        const nameErr = getFieldError("name", form.name);
        const emailErr = getFieldError("email", form.email);
        return !nameErr && !emailErr;
    };

    const handleNext1 = () => {
        const touched = { name: true, email: true, phone: true };
        setFormTouched(touched);
        setFormErrors({
            name: getFieldError("name", form.name),
            email: getFieldError("email", form.email),
            phone: getFieldError("phone", form.phone),
        });
        if (!step1Valid()) return;
        setStep(2);
    };

    // ── Step 2 ──────────────────────────────────────────────
    const handleMerchantChange = (key, val) => {
        setMerchant((prev) => ({ ...prev, [key]: val }));
        const err = val.trim() ? "" : "此欄位為必填";
        setMerchantErrors((prev) => ({ ...prev, [key]: err }));
    };

    const handleMerchantBlur = (key) => {
        setMerchantTouched((prev) => ({ ...prev, [key]: true }));
        setMerchantErrors((prev) => ({ ...prev, [key]: merchant[key].trim() ? "" : "此欄位為必填" }));
    };

    const step2Valid = () => {
        return merchant.name.trim() && merchant.branch.trim()
            && merchant.region.trim() && merchant.address.trim();
    };

    const handleNext2 = () => {
        const touched = { name: true, branch: true, region: true, address: true };
        setMerchantTouched(touched);
        setMerchantErrors({
            name: merchant.name.trim() ? "" : "請輸入商戶名稱",
            branch: merchant.branch.trim() ? "" : "請輸入分店名稱",
            region: merchant.region.trim() ? "" : "請輸入所屬地區",
            address: merchant.address.trim() ? "" : "請輸入分店地址",
        });
        if (!step2Valid()) return;
        setStep(3);
    };

    // ── Step 3 ──────────────────────────────────────────────
    const pwError = pwTouched
        ? (!password ? "請設定密碼" : password.length < 6 ? "密碼至少需要 6 位" : "")
        : "";
    const confirmError = confirmTouched
        ? (!confirmPw ? "請確認密碼" : password !== confirmPw ? "兩次密碼輸入不一致" : "")
        : "";

    const step3Valid = () => password.length >= 6 && password === confirmPw && !!password;

    const handleSubmit = (e) => {
        e.preventDefault();
        setPwTouched(true);
        setConfirmTouched(true);
        setGlobalError("");
        if (!step3Valid()) {
            if (!password) setGlobalError("請設定密碼");
            else if (password.length < 6) setGlobalError("密碼至少需要 6 位");
            else if (password !== confirmPw) setGlobalError("兩次密碼輸入不一致");
            return;
        }
        setDone(true);
    };

    // ── Done ──────────────────────────────────────────────
    if (done) return (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
            <h3 style={{ color: t.text, fontSize: 18, margin: "0 0 8px" }}>註冊申請已提交</h3>
            <p style={{ color: t.textMuted, fontSize: 13, margin: "0 0 6px" }}>
                {merchant.name} · {merchant.region} · {merchant.branch}
            </p>
            <p style={{ color: t.textFaint, fontSize: 12, margin: "0 0 24px" }}>
                帳戶審核後將發送確認 Email 至<br />
                <span style={{ fontFamily: "monospace", color: t.accentText }}>{form.email}</span>
            </p>
            <button onClick={onDone} style={{
                padding: "10px 28px", background: t.accent, border: "none",
                color: "white", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600
            }}>返回登入</button>
        </div>
    );

    return (
        <div>
            {/* Progress */}
            <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
                {steps.map((label, i) => {
                    const active = step === i + 1; const isDone = step > i + 1;
                    return (
                        <div key={label} style={{ flex: 1, textAlign: "center" }}>
                            <div style={{ height: 3, borderRadius: 4, marginBottom: 4, transition: "background 0.3s", background: isDone || active ? t.accent : t.border }} />
                            <span style={{ fontSize: 10, fontWeight: isDone || active ? 700 : 400, color: isDone || active ? t.accent : t.textFaint }}>
                                {isDone ? "✓ " : ""}{label}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* ── Step 1: 基本資料 ── */}
            {step === 1 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {/* 姓名 */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ color: t.textMuted, fontSize: 13, fontWeight: 500 }}>姓名 *</label>
                        <input type="text" value={form.name}
                            onChange={(e) => handleFormChange("name", e.target.value)}
                            onBlur={() => handleFormBlur("name")}
                            placeholder="您的全名"
                            style={inputBorder(formTouched.name && formErrors.name)} />
                        <FieldError msg={formTouched.name ? formErrors.name : ""} />
                    </div>

                    {/* Email */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ color: t.textMuted, fontSize: 13, fontWeight: 500 }}>Email *</label>
                        <input type="email" value={form.email}
                            onChange={(e) => handleFormChange("email", e.target.value)}
                            onBlur={() => handleFormBlur("email")}
                            placeholder="your@email.com"
                            style={inputBorder(formTouched.email && formErrors.email)} />
                        <FieldError msg={formTouched.email ? formErrors.email : ""} />
                    </div>

                    {/* 電話 */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ color: t.textMuted, fontSize: 13, fontWeight: 500 }}>聯絡電話（可選）</label>
                        <input type="tel" value={form.phone}
                            onChange={(e) => handleFormChange("phone", e.target.value)}
                            onBlur={() => handleFormBlur("phone")}
                            placeholder="+852 XXXX XXXX"
                            style={inputBorder(formTouched.phone && formErrors.phone)} />
                        <FieldError msg={formTouched.phone ? formErrors.phone : ""} />
                    </div>

                    <button type="button" onClick={handleNext1} style={{
                        padding: "11px", background: t.accent, border: "none",
                        color: "white", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600
                    }}>下一步 →</button>
                </div>
            )}

            {/* ── Step 2: 商戶資料 ── */}
            {step === 2 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <p style={{ color: t.textMuted, fontSize: 13, margin: 0 }}>請輸入您的商戶及分店資料：</p>

                    {[
                        { label: "商戶名稱 *", key: "name", placeholder: "例如：屈臣氏" },
                        { label: "分店名稱 *", key: "branch", placeholder: "例如：銅鑼灣店" },
                        { label: "所屬地區 *", key: "region", placeholder: "例如：香港島" },
                        { label: "分店地址 *", key: "address", placeholder: "例如：銅鑼灣軒尼詩道 555 號" },
                    ].map((f) => (
                        <div key={f.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <label style={{ color: t.textMuted, fontSize: 13, fontWeight: 500 }}>{f.label}</label>
                            <input type="text" value={merchant[f.key]}
                                onChange={(e) => handleMerchantChange(f.key, e.target.value)}
                                onBlur={() => handleMerchantBlur(f.key)}
                                placeholder={f.placeholder}
                                style={inputBorder(merchantTouched[f.key] && merchantErrors[f.key])} />
                            <FieldError msg={merchantTouched[f.key] ? merchantErrors[f.key] : ""} />
                        </div>
                    ))}

                    <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" onClick={() => { setStep(1); setGlobalError(""); }} style={{
                            padding: "10px 18px", background: "transparent", border: `1px solid ${t.border}`,
                            color: t.textMuted, borderRadius: 8, cursor: "pointer", fontSize: 14
                        }}>← 返回</button>
                        <button type="button" onClick={handleNext2} style={{
                            flex: 1, padding: "10px", background: t.accent, border: "none",
                            color: "white", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600
                        }}>下一步 →</button>
                    </div>
                </div>
            )}

            {/* ── Step 3: 設定密碼 ── */}
            {step === 3 && (
                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div style={{ padding: "10px 14px", background: t.inputBg, border: `1px solid ${t.border}`, borderRadius: 8 }}>
                        <p style={{ color: t.textFaint, fontSize: 11, fontWeight: 700, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>📋 註冊摘要</p>
                        {[
                            { label: "姓名", value: form.name },
                            { label: "Email", value: form.email, mono: true },
                            ...(form.phone ? [{ label: "電話", value: form.phone }] : []),
                            { label: "商戶", value: merchant.name },
                            { label: "分店", value: `${merchant.region} › ${merchant.branch}` },
                        ].map((row) => (
                            <div key={row.label} style={{ display: "flex", gap: 10, marginBottom: 3 }}>
                                <span style={{ color: t.textFaint, fontSize: 11, minWidth: 36 }}>{row.label}</span>
                                <span style={{ color: t.text, fontSize: 11, fontFamily: row.mono ? "monospace" : "inherit" }}>{row.value}</span>
                            </div>
                        ))}
                    </div>

                    {/* 設定密碼 */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ color: t.textMuted, fontSize: 13, fontWeight: 500 }}>設定密碼 *</label>
                        <input type="password" value={password}
                            onChange={(e) => { setPassword(e.target.value); setGlobalError(""); }}
                            onBlur={() => setPwTouched(true)}
                            placeholder="至少 6 位"
                            style={inputBorder(pwTouched && pwError)} />
                        <FieldError msg={pwError} />
                        {password.length > 0 && !pwError && (
                            <div>
                                <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                                    {[1, 2, 3, 4].map((level) => (
                                        <div key={level} style={{
                                            flex: 1, height: 4, borderRadius: 4,
                                            background: level <= pwStrength ? pwColors[pwStrength] : t.border
                                        }} />
                                    ))}
                                </div>
                                <span style={{ color: t.textFaint, fontSize: 11 }}>{pwLabels[pwStrength]}</span>
                            </div>
                        )}
                    </div>

                    {/* 確認密碼 */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ color: t.textMuted, fontSize: 13, fontWeight: 500 }}>確認密碼 *</label>
                        <input type="password" value={confirmPw}
                            onChange={(e) => { setConfirmPw(e.target.value); setGlobalError(""); }}
                            onBlur={() => setConfirmTouched(true)}
                            placeholder="再次輸入密碼"
                            style={inputBorder(confirmTouched && confirmError)} />
                        <FieldError msg={confirmError} />
                    </div>

                    {globalError && <p style={{ color: t.danger, fontSize: 13, margin: 0 }}>⚠️ {globalError}</p>}

                    <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" onClick={() => { setStep(2); setGlobalError(""); setPwTouched(false); setConfirmTouched(false); }} style={{
                            padding: "10px 18px", background: "transparent", border: `1px solid ${t.border}`,
                            color: t.textMuted, borderRadius: 8, cursor: "pointer", fontSize: 14
                        }}>← 返回</button>
                        <button type="submit" style={{
                            flex: 1, padding: "10px", border: "none", color: "white",
                            borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600,
                            background: `linear-gradient(135deg, #1a3a5c 0%, #2a9d8f 100%)`
                        }}>✅ 提交註冊申請</button>
                    </div>
                </form>
            )}
        </div>
    );
}
